import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  KULLANICI_ID: "",
  SIRKET_ADI: "",
  VERGI_NO: "",
  ADRES: "",
};

const KurumsalBilgiler = () => {
  document.title = "Kurumsal Bilgiler | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/KURUMSAL_BILGILER?$orderby=ID desc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Kurumsal bilgiler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      KULLANICI_ID: row.KULLANICI_ID ?? "",
      SIRKET_ADI: row.SIRKET_ADI || "",
      VERGI_NO: row.VERGI_NO || "",
      ADRES: row.ADRES || "",
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.SIRKET_ADI.trim()) {
      setError("Şirket adı zorunludur.");
      return;
    }
    if (formData.KULLANICI_ID === "" || isNaN(Number(formData.KULLANICI_ID))) {
      setError("Geçerli bir kullanıcı ID giriniz.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        KULLANICI_ID: Number(formData.KULLANICI_ID),
        SIRKET_ADI: formData.SIRKET_ADI.trim(),
        VERGI_NO: formData.VERGI_NO || null,
        ADRES: formData.ADRES || null,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/KURUMSAL_BILGILER(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Kurumsal bilgi güncellendi.");
      } else {
        await fetch(`${BASE_URL}/KURUMSAL_BILGILER`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Kurumsal bilgi eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kurumsal bilgi kaydını silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/KURUMSAL_BILGILER(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Kurumsal bilgi silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Kullanıcı ID",
      accessorKey: "KULLANICI_ID",
      enableSorting: true,
      cell: (c) => <Badge color="light" className="text-dark">#{c.getValue() ?? "-"}</Badge>,
    },
    {
      header: "Şirket Adı",
      accessorKey: "SIRKET_ADI",
      enableSorting: true,
      cell: (c) => <span className="fw-medium">{c.getValue() || "---"}</span>,
    },
    {
      header: "Vergi No",
      accessorKey: "VERGI_NO",
      enableSorting: true,
      cell: (c) => c.getValue() || <span className="text-muted">---</span>,
    },
    {
      header: "Adres",
      accessorKey: "ADRES",
      enableSorting: false,
      cell: (c) => {
        const v = c.getValue() || "";
        return <span className="text-muted">{v.length > 60 ? v.slice(0, 60) + "…" : (v || "---")}</span>;
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İçerik Yönetimi" breadcrumbItem="Kurumsal Bilgiler" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Kurumsal / Şirket Bilgileri</h4>
                      <p className="text-muted small mb-0">
                        Satıcıların şirket adı, vergi no ve adres bilgilerini buradan yönetin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Kayıt
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Şirket / vergi no ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-office-building-cog-outline" : "mdi-office-building-plus-outline"} me-2 text-primary`}></i>
          {isEditing ? `Kurumsal Bilgi Düzenle (#${formData.ID})` : "Yeni Kurumsal Bilgi"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Kullanıcı ID <span className="text-danger">*</span></Label>
                <Input
                  type="number"
                  value={formData.KULLANICI_ID}
                  onChange={(e) => f("KULLANICI_ID", e.target.value)}
                  placeholder="Örn: 125"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className="fw-bold">Vergi No</Label>
                <Input
                  value={formData.VERGI_NO}
                  onChange={(e) => f("VERGI_NO", e.target.value)}
                  placeholder="Vergi numarası"
                  maxLength={50}
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Şirket Adı <span className="text-danger">*</span></Label>
                <Input
                  value={formData.SIRKET_ADI}
                  onChange={(e) => f("SIRKET_ADI", e.target.value)}
                  placeholder="Şirket / ünvan"
                  maxLength={250}
                />
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Adres</Label>
                <Input
                  type="textarea"
                  rows={4}
                  value={formData.ADRES}
                  onChange={(e) => f("ADRES", e.target.value)}
                  placeholder="Açık adres"
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default KurumsalBilgiler;
